import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Radar, ArrowUpRight, Globe, Phone } from 'lucide-react';
import { SUPPORT_PHONE, SUPPORT_PHONE_HREF } from '../config/contact';
import AcceptedCards from './PaymentIcons';

const Footer = () => {
  const [currentLang, setCurrentLang] = useState(localStorage.getItem('googleTranslateLang') || 'en');

  const changeLanguage = (lang) => {
    if (lang === 'en') {
      document.cookie = 'googtrans=; path=/; max-age=0';
      localStorage.removeItem('googleTranslateLang');
    } else {
      document.cookie = `googtrans=/en/${lang}; path=/; max-age=31536000`;
      localStorage.setItem('googleTranslateLang', lang);
    }
    setCurrentLang(lang);
    window.location.reload();
  };

  const footerLinks = {
    Product: [
      { label: 'Features', path: '/features' },
      { label: 'Technology', path: '/technology' },
      { label: 'Find My', path: '/find-my' },
      { label: 'Training', path: '/training' },
      { label: 'Shop', path: '/shop' },
    ],
    Company: [
      { label: 'About', path: '/about' },
      { label: 'Impact', path: '/impact' },
      { label: 'Press', path: '/press' },
      { label: 'Partner Program', path: '/partner' },
      { label: 'Blog', path: '/blog' },
      { label: 'Contact', path: '/contact' },
    ],
    Support: [
      { label: 'Track Order', path: '/track' },
      { label: 'Warranty', path: '/warranty' },
      { label: 'Safety Notice', path: '/safety-notice' },
      { label: 'Terms', path: '/terms' },
      { label: 'Privacy', path: '/privacy' },
      { label: 'Refund', path: '/refund' },
    ],
  };

  return (
    <footer data-testid="footer" className="bg-[#0a0a0a] border-t border-gray-800/50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Main Footer */}
        <div className="py-16 grid grid-cols-1 md:grid-cols-5 gap-12">
          {/* Brand Column */}
          <div className="md:col-span-2">
            <Link to="/" className="flex items-center space-x-2 mb-6">
              <div className="w-10 h-10 rounded-full bg-gradient-to-br from-blue-400 to-blue-600 flex items-center justify-center">
                <Radar className="w-6 h-6 text-white" />
              </div>
              <span className="text-2xl font-semibold tracking-tight">ClearWay</span>
            </Link>
            <p className="text-gray-400 text-sm leading-relaxed max-w-sm mb-4">
              ClearWay is a smart cane (smart mobility stick) with obstacle detection sensors. Field-tested with blind students in Mumbai (Trial 1, April 2026) — actively improving.
            </p>

            {/* Creator & Trust Info */}
            <div className="text-xs text-gray-500 mb-4">
              <p className="mb-1">Created by <span className="text-gray-300">Jaiveer Talwar</span></p>
              <p>Operated by Clearway Mobility LLP</p>
            </div>

            {/* Shipping Info */}
            <div className="flex items-center gap-2 text-gray-400 text-sm mb-4">
              <Globe className="w-4 h-4 text-blue-400" />
              <span>Currently shipping to <strong className="text-white">India</strong> & <strong className="text-white">UAE</strong></span>
            </div>

            {/* Support Phone */}
            <a
              href={SUPPORT_PHONE_HREF}
              data-testid="footer-support-phone"
              className="flex items-center gap-2 text-gray-400 hover:text-white text-sm mb-4 transition-colors w-fit"
            >
              <Phone className="w-4 h-4 text-blue-400" />
              <span>Support: <strong className="text-white">{SUPPORT_PHONE}</strong></span>
            </a>

            <Link
              to="/partner"
              className="inline-flex items-center gap-2 text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
            >
              Partner Program
              <ArrowUpRight className="w-4 h-4" />
            </Link>
          </div>

          {/* Links Columns */}
          {Object.entries(footerLinks).map(([category, links]) => (
            <div key={category}>
              <h4 className="text-white font-medium mb-4">{category}</h4>
              <ul className="space-y-3">
                {links.map((link) => (
                  <li key={link.path}>
                    <Link
                      to={link.path}
                      className="text-gray-400 hover:text-white text-sm transition-colors"
                    >
                      {link.label}
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        {/* Bottom Footer */}
        <div className="py-8 border-t border-gray-800/30">
          <div className="flex flex-col md:flex-row items-center justify-between gap-4">
            <div className="flex flex-col md:flex-row items-center gap-4">
              <p className="text-gray-500 text-sm" data-testid="student-project-note">
                ClearWay — A product by Clearway Mobility LLP
              </p>

              {/* Language Switch */}
              <div className="flex items-center gap-2 text-sm text-gray-400 bg-gray-900/50 px-3 py-1.5 rounded-full border border-gray-800 transition-colors hover:border-gray-700">
                <Globe className="w-4 h-4" />
                <select
                  className="bg-transparent border-none text-gray-300 focus:ring-0 cursor-pointer outline-none text-sm appearance-none pr-2"
                  aria-label="Select language"
                  onChange={(e) => changeLanguage(e.target.value)}
                  value={currentLang}
                >
                  <option value="en" className="bg-[#0a0a0a]">English</option>
                  <option value="hi" className="bg-[#0a0a0a]">हिन्दी (Hindi)</option>
                  <option value="mr" className="bg-[#0a0a0a]">मराठी (Marathi)</option>
                  <option value="ar" className="bg-[#0a0a0a]">العربية (Arabic)</option>
                </select>
              </div>
            </div>
            <div className="flex flex-col md:flex-row items-center gap-4">
              <div className="flex items-center gap-2">
                <span className="text-gray-500 text-xs">We accept</span>
                <AcceptedCards />
              </div>
              <p className="text-gray-600 text-sm">
                © {new Date().getFullYear()} Clearway Mobility LLP. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
